import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Upload, Trash2, Plane } from "lucide-react";
import { toast } from "sonner";

export default function CsvImport({ onImported }) {
  const fileRef = useRef(null);
  const [flights, setFlights] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/presence/flights");
      setFlights(data);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setErrors([]);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/presence/flights/csv", form);
      toast.success(`Imported ${data.imported} flight${data.imported !== 1 ? "s" : ""}${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicates skipped` : ""}`);
      setErrors(data.errors || []);
      await load();
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/presence/flights/${id}`);
      await load();
      onImported?.();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="rounded-card border border-hairline bg-surface h-full flex flex-col" data-testid="csv-import">
      <div className="px-5 py-4 border-b border-sand-200 flex items-center justify-between gap-3">
        <div>
          <p className="label-tag">Flight history</p>
          <p className="font-mono text-[10px] text-sand-400 mt-1">CSV: date, from, to [, flight, airline, arrival_date]</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="csv-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="btn-swiss btn-primary text-xs disabled:opacity-50"
          data-testid="csv-upload-btn"
        >
          <Upload size={14} className="mr-2" /> {busy ? "Uploading..." : "Upload CSV"}
        </button>
      </div>

      {errors.length > 0 && (
        <div className="px-5 py-3 border-b border-sand-200 bg-[#F9F0EC]" data-testid="csv-errors">
          {errors.map((er, i) => (
            <p key={i} className="font-mono text-[11px] text-error">Line {er.line}: {er.error}</p>
          ))}
        </div>
      )}

      {flights.length === 0 ? (
        <p className="px-5 py-8 text-sm text-sand-500">No imported flights yet. Forwarded email flights are counted automatically.</p>
      ) : (
        <ul className="divide-y divide-sand-50 max-h-72 overflow-y-auto">
          {flights.map((f) => (
            <li key={f.id} className="px-5 py-2.5 flex items-center gap-3" data-testid={`history-flight-${f.id}`}>
              <Plane size={13} className="text-sand-400 shrink-0" />
              <span className="font-mono text-xs text-sand-500 w-24 shrink-0">{(f.dep_dt || "").slice(0, 10)}</span>
              <span className="font-mono text-sm text-sand-900 flex-1 truncate">
                {f.dep_iata} → {f.arr_iata}
                {f.flight_number && <span className="text-sand-400 ml-2 text-xs">{f.flight_number}</span>}
              </span>
              <button
                onClick={() => remove(f.id)}
                className="text-sand-300 hover:text-error transition-colors"
                title="Delete"
                data-testid={`delete-flight-${f.id}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
